/**
 * API: alertas v2 (Sprint 6 + Sprint 15).
 *
 * Centro de comando — fetchAlerts (lista), fetchAlertsSummary (counts
 * por bucket), patchAlertAction (atender / posponer).
 */

import { apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken, request } from "./_client";

export interface AlertDTO {
  id: string;
  alert_type: string;
  severity: "info" | "warning" | "critical";
  title: string;
  body: string;
  related_opportunity_id: string | null;
  created_at: string;
  expires_at: string | null;
  // Sprint 15.1 — campos nuevos
  atendida_at: string | null;
  snoozed_until: string | null;
  cta_href: string | null;
  context: Record<string, unknown> | null;
}

export interface AlertsPage {
  items: AlertDTO[];
  total: number;
}

export interface AlertSummary {
  critical_today: number;
  this_week: number;
  informational: number;
  total_active: number;
  attended_this_week: number;
}

/**
 * Maximo que acepta el backend (`limit` <= 200). Sin `limit` el backend
 * devuelve 50 y la pagina contaba solo esos, contradiciendo a la banda
 * (#564). Se pide todo lo activo de una vez; `total` sigue trayendo el
 * conteo sin paginar para avisar si aun asi la lista quedo recortada.
 */
export const ALERTS_FETCH_LIMIT = 200;

export async function fetchAlerts(
  municipality: string,
  options: { includeAttended?: boolean } = {},
): Promise<AlertsPage> {
  const token = await getAccessToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const qs = new URLSearchParams({ municipality, limit: String(ALERTS_FETCH_LIMIT) });
  if (options.includeAttended) qs.set("include_attended", "true");

  const res = await safeFetch(`${API_V1}/alerts?${qs.toString()}`, { headers });
  if (res.status === 401) await supabase.auth.signOut();
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
  const items = (await res.json()) as AlertDTO[];
  const totalHeader = res.headers.get("X-Total-Count");
  const total = totalHeader !== null ? Number(totalHeader) : items.length;
  return { items, total };
}

/** Counts por bucket — banda del header de /alertas (Sprint 15). */
export function fetchAlertsSummary(municipality: string): Promise<AlertSummary> {
  return request<AlertSummary>(`/alerts/summary?municipality=${encodeURIComponent(municipality)}`);
}

/** Marca atendida o snooze 1 semana. 204 si OK, 404 si no encontrada. */
export async function patchAlertAction(args: {
  alertId: string;
  municipality: string;
  action: "ack" | "snooze_week";
}): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const url = `${API_V1}/alerts/${encodeURIComponent(args.alertId)}?municipality=${encodeURIComponent(args.municipality)}`;
  const res = await safeFetch(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ action: args.action }),
  });
  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
}
